import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Download, 
  Share2, 
  Copy, 
  Check,
  Loader2,
  ImageIcon,
  Mail,
  MessageSquare,
  Sparkles,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Folder,
  X
} from "lucide-react";
import { toast } from "sonner";
import QRCode from "react-qr-code";

// Social Media Card Categories with icons
const CARD_CATEGORIES = [
  { id: "keepthe100", name: "Keep the 100", icon: "🏆", color: "from-amber-500 to-yellow-500" },
  { id: "founding", name: "Founding Partners", icon: "⭐", color: "from-purple-500 to-violet-500" },
  { id: "barbers", name: "Barbers", icon: "✂️", color: "from-blue-500 to-cyan-500" },
  { id: "stylists", name: "Stylists (ATL)", icon: "💇", color: "from-pink-500 to-rose-500" },
  { id: "diverse", name: "Diverse Teams", icon: "🌍", color: "from-green-500 to-emerald-500" },
  { id: "superSoldier", name: "Super Soldier", icon: "🎖️", color: "from-orange-500 to-red-500" },
  { id: "recruitment", name: "Recruitment", icon: "📣", color: "from-indigo-500 to-purple-500" }
];

// Social Media Card Images - Comprehensive Marketing Kit
const BOOTH_RENT_CARDS = [
  // === KEEP THE 100 LOOK FEST CAMPAIGN ===
  {
    id: "keepthe100-black-braider",
    name: "Black Female Braider",
    image: "https://static.prod-images.emergentagent.com/jobs/40f0a222-0f89-4622-a9e5-a12a0a74d69c/images/ec813b59f7ffded5cef52bb8d97cfcfecfb88118df2e05659d35f3e6bc29a276.png",
    category: "keepthe100",
    headline: "KEEP THE 100 - Win $100 Monthly!"
  },
  {
    id: "keepthe100-black-barber-1",
    name: "Black Male Barber",
    image: "https://static.prod-images.emergentagent.com/jobs/40f0a222-0f89-4622-a9e5-a12a0a74d69c/images/15259cc9105d9855101f4037f4fe5d739cb13eb959bab2ac0451c67941d3aa2a.png",
    category: "keepthe100",
    headline: "KEEP THE 100 - Zero Platform Fees"
  },
  {
    id: "keepthe100-black-stylist",
    name: "Black Female Stylist",
    image: "https://static.prod-images.emergentagent.com/jobs/40f0a222-0f89-4622-a9e5-a12a0a74d69c/images/e4ffbb51aa35e16ed0b2cede181d98f531d275caea9839877ebb15ca52228cb3.png",
    category: "keepthe100",
    headline: "KEEP THE 100 - Your Chair, Your Money"
  },
  {
    id: "keepthe100-black-barber-2",
    name: "Black Barber Gold Clippers",
    image: "https://static.prod-images.emergentagent.com/jobs/40f0a222-0f89-4622-a9e5-a12a0a74d69c/images/aae4793c2d9414965e638ee810cd0ca228521da0eb62f378683c8955cd60df82.png",
    category: "keepthe100",
    headline: "KEEP THE 100 - Look Fest Contest"
  },
  {
    id: "keepthe100-black-duo",
    name: "Black Barber & Stylist Duo",
    image: "https://static.prod-images.emergentagent.com/jobs/40f0a222-0f89-4622-a9e5-a12a0a74d69c/images/62421763fefbae15d67afc55366ba06662f2e2cf4445c11c253cde1408002a4f.png",
    category: "keepthe100",
    headline: "KEEP THE 100 - Join the Movement"
  },
  {
    id: "keepthe100-white-barber",
    name: "White Male Barber",
    image: "https://static.prod-images.emergentagent.com/jobs/40f0a222-0f89-4622-a9e5-a12a0a74d69c/images/371ae7e3b779778a42e92e26b64137396ad94757fed953f3190035a89d1a1080.png",
    category: "keepthe100",
    headline: "KEEP THE 100 - Win $100 Monthly!"
  },
  {
    id: "keepthe100-white-stylist",
    name: "White Female Stylist",
    image: "https://static.prod-images.emergentagent.com/jobs/40f0a222-0f89-4622-a9e5-a12a0a74d69c/images/76c2c2356ac7faf35b30375eabd99748c186e687cef238f3c2a1526545686d16.png",
    category: "keepthe100",
    headline: "KEEP THE 100 - Zero Platform Fees"
  },
  {
    id: "keepthe100-latino-barber",
    name: "Latino Male Barber",
    image: "https://static.prod-images.emergentagent.com/jobs/40f0a222-0f89-4622-a9e5-a12a0a74d69c/images/e9d1a5491f08da094231e74bf6afe0ee23e0e906d0d181936d6fa827867c9e12.png",
    category: "keepthe100",
    headline: "KEEP THE 100 - Your Chair, Your Money"
  },
  {
    id: "keepthe100-latina-stylist",
    name: "Latina Female Stylist",
    image: "https://static.prod-images.emergentagent.com/jobs/40f0a222-0f89-4622-a9e5-a12a0a74d69c/images/085d483beb788bd95a9f1324a0798e3fcbd3340c36cbbdd09c48cd744a3c3e97.png",
    category: "keepthe100",
    headline: "KEEP THE 100 - Look Fest Contest"
  },
  {
    id: "keepthe100-polynesian-barber",
    name: "Polynesian Male Barber",
    image: "https://static.prod-images.emergentagent.com/jobs/40f0a222-0f89-4622-a9e5-a12a0a74d69c/images/eb70d33e2a81b2267d7c7a5ebc0d11a7ce8fd93feee8baf91601895a3a4cc60d.png",
    category: "keepthe100",
    headline: "KEEP THE 100 - Join the Movement"
  },
  {
    id: "keepthe100-polynesian-stylist",
    name: "Polynesian Female Stylist",
    image: "https://static.prod-images.emergentagent.com/jobs/40f0a222-0f89-4622-a9e5-a12a0a74d69c/images/93a43ad1b715fdab1430eb3d16471953b50bd923ee7146ef11418b36680314b5.png",
    category: "keepthe100",
    headline: "KEEP THE 100 - Win $100 Monthly!"
  },

  // === FOUNDING PARTNER PROGRAM ===
  {
    id: "soldiers-mixed-unity",
    name: "Building Future Together",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/fb24dd864e3419c261404706cdd3c0765d1278ab1e0e758239d4ec85f4756b28.png",
    category: "founding",
    headline: "Building the Future of Beauty Together"
  },
  {
    id: "soldiers-elite-founders",
    name: "Earn While You Grow",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/ab53bacb9717f9f878e46f819a0c64aea2ae33d951163e44fa83840c71e6949a.png",
    category: "founding",
    headline: "Earn While You Grow - Founding Partners"
  },
  {
    id: "soldiers-women-diverse",
    name: "Elite 50 Women",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/1b46850f3537da965b3f0ae5c86e97685231ac3354a554255764b396065fff65.png",
    category: "founding",
    headline: "Join the Elite 50 - Founding Partners"
  },
  {
    id: "soldiers-diverse-group",
    name: "HairChair Soldiers",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/b5236de6b17350865e85e9e8f80c08aff7c401c7b08a681b6eab5ff2d6225473.png",
    category: "founding",
    headline: "HairChair Soldiers - Founding Partner Program"
  },
  {
    id: "soldiers-men-barbers",
    name: "Barber Soldiers",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/098ddcee21ed313201582026f2e81efbd9ca2fe2442bc2f567402a2a2a7b24a6.png",
    category: "founding",
    headline: "Barber Soldiers - First 50 Founding Partners"
  },
  
  // === BLACK BARBERS & STYLISTS ===
  {
    id: "black-barber-lineup",
    name: "Lineup - Founding Perks",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/3c1ff12e3caf0b10741531f88bde181ab84a5ab9cb73bce4f711f8594faacc78.png",
    category: "barbers",
    headline: "First 50 Get Founding Partner Perks"
  },
  {
    id: "black-barber-locs",
    name: "Locs - Zero Fees",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/2cd631ca54c96a8ea352685e48397119753ae71371f829d3c95f78e1c63604c7.png",
    category: "barbers",
    headline: "0% Fees on Cash, Zelle & CashApp"
  },
  {
    id: "black-barber-fade",
    name: "Fade - Own Success",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/b2ea7d2b50252e652709d576926b3b014b2c027beb985ffff203b47f7b35bf3d.png",
    category: "barbers",
    headline: "Own Your Schedule. Own Your Success."
  },
  {
    id: "black-loctician",
    name: "Loctician - Your Terms",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/3ac5cdf9ee1d84c9ad291b7e901f1cf9080ba19b66450dafe8b5def359ea9788.png",
    category: "barbers",
    headline: "Your Talent. Your Terms."
  },
  {
    id: "black-barber-edge",
    name: "Edge Up - Pay Direct",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/0c1ff2c6aaab2b19e4fc334f1c3e0784f66bd89cbaa128393277b725b8e620de.png",
    category: "barbers",
    headline: "Skip the Crazy Fees – Pay Direct"
  },
  
  // === ATL STYLISTS (WEAVES, BRAIDS, EXTENSIONS) ===
  {
    id: "atl-weave-future",
    name: "Weave - Future Booking",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/fa9d82bec8037372358946f4e4f14bd758cb6b6dde81fabc7e05af35d0d9c8c4.png",
    category: "stylists",
    headline: "The Future of Booking is Here"
  },
  {
    id: "atl-braids-paid",
    name: "Braids - Get Paid Faster",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/26dffbf5c340057232591b2f2aa291fdaf5b31f19497522409ed75950962c074.png",
    category: "stylists",
    headline: "Get Paid Faster. Work Smarter."
  },
  {
    id: "atl-red-weave",
    name: "Red Weave - Movement",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/3e84d34607cd6495d5c512fb8f387c66c627038d058fe04a12985419d91d17f0.png",
    category: "stylists",
    headline: "More Than an App – A Movement"
  },
  {
    id: "atl-locs-legacy",
    name: "Locs - Your Legacy",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/e34f0ac266c952f6166106033681f533b65cd79ee040cf26f487f87545a76156.png",
    category: "stylists",
    headline: "Your Chair. Your Legacy."
  },
  {
    id: "atl-ponytail-partners",
    name: "Ponytail - Partners Wanted",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/94aa9906a5479e9e40346f954458067ce520572d90375e8f83adbea2295fab7d.png",
    category: "stylists",
    headline: "Founding Partners Wanted"
  },
  {
    id: "atl-muscular-male-weave",
    name: "Male Stylist - Weave",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/26ab8b3c3a62bb2b13b7cb0d44ea260f79fb08ec35fd1adf317d52f32ca8d5b2.png",
    category: "stylists",
    headline: "YOUR CHAIR. YOUR RULES."
  },
  {
    id: "atl-tattoo-barber",
    name: "Tattoo Barber",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/d14b88ff4e3924d0f1cd7fabd68de9af9f683b0e2d8a022200de5eebdc9cc191.png",
    category: "stylists",
    headline: "Built BY Stylists FOR Stylists"
  },
  
  // === WHITE & LATINO STYLISTS ===
  {
    id: "white-barber-founding",
    name: "White Barber - Founding",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/3d8ca1ad208e5867607f8cdb529e9dbb1ff4deba4579d84cc799a42c87caf225.png",
    category: "diverse",
    headline: "First 50 Get Founding Partner Perks"
  },
  {
    id: "white-stylist-future",
    name: "White Stylist - Future",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/42ff185b7002d9fdad9b57d965de6c6533ff232f5ab3eba4268995b59a875e46.png",
    category: "diverse",
    headline: "The Future of Booking is Here"
  },
  {
    id: "white-barber-partners",
    name: "White Barber - Partners",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/159c43acf4cd790024c4786cdff56d9f33abaebfdd1387338fe1fa7fe6a62d33.png",
    category: "diverse",
    headline: "Founding Partners Wanted"
  },
  {
    id: "white-female-success",
    name: "White Female - Success",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/a03b9b084d2eb642c1949f5d78524cc4bffbbb8cb92a9b865033933d810af654.png",
    category: "diverse",
    headline: "Own Your Schedule. Own Your Success."
  },
  {
    id: "white-stylist-legacy",
    name: "White Stylist - Legacy",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/1a00b2b0a21a1e2e3c24d0303dda930b3412afc0da27fb94aa443165e3895bca.png",
    category: "diverse",
    headline: "Your Chair. Your Legacy."
  },
  {
    id: "latino-barber-zerofees",
    name: "Latino Barber - Zero Fees",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/5c6d0710587f6499c974fd4a157875b223e3e72761a4d5d6c6a641448efd4408.png",
    category: "diverse",
    headline: "0% Fees on Cash, Zelle & CashApp"
  },
  {
    id: "latino-barber-skipfees",
    name: "Latino Barber - Skip Fees",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/4b5f4246137e0f1a3f12f7a812ad48d2dc2ab55090256c5aed92505a44ab0743.png",
    category: "diverse",
    headline: "Skip the Crazy Fees – Pay Direct"
  },
  {
    id: "latina-stylist-terms",
    name: "Latina Stylist - Terms",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/63c775186103ece561f8522294553bb359b22bbdbea552e39e5773109ef0daa0.png",
    category: "diverse",
    headline: "Your Talent. Your Terms."
  },
  {
    id: "latina-stylist-paid",
    name: "Latina Stylist - Paid Faster",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/62e031516b86ab823df3f8f4246d60e5f46c2cc8c29edc3bc75d05dbb923af48.png",
    category: "diverse",
    headline: "Get Paid Faster. Work Smarter."
  },
  {
    id: "latina-glam-movement",
    name: "Latina Glam - Movement",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/6a5c207a0107962fdda7b9b9c1aaae948f949f4c601450a6fab2002e4543bd4a.png",
    category: "diverse",
    headline: "More Than an App – A Movement"
  },
  
  // === ORIGINAL RECRUITMENT CARDS ===
  {
    id: "african",
    name: "Professional African",
    image: "https://customer-assets.emergentagent.com/job_stripe-stylist/artifacts/wbot88x0_Chasing%20Booth%20Rent%20African.png",
    category: "recruitment",
    headline: "Your Chair. Your Rules."
  },
  {
    id: "black",
    name: "Professional Black",
    image: "https://customer-assets.emergentagent.com/job_stripe-stylist/artifacts/d6q8cwqu_Chasing%20Booth%20Rent%20Black.png",
    category: "recruitment",
    headline: "Your Chair. Your Rules."
  },
  {
    id: "latino",
    name: "Professional Latino",
    image: "https://customer-assets.emergentagent.com/job_stripe-stylist/artifacts/whz64evg_Chasing%20Booth%20Rent%20Latino.png",
    category: "recruitment",
    headline: "Your Chair. Your Rules."
  },
  {
    id: "white",
    name: "Professional White",
    image: "https://customer-assets.emergentagent.com/job_stripe-stylist/artifacts/rrk63q1l_Chasing%20Booth%20Rent%20White.png",
    category: "recruitment",
    headline: "Your Chair. Your Rules."
  },
  {
    id: "outreach",
    name: "Stylist Outreach",
    image: "https://customer-assets.emergentagent.com/job_stripe-stylist/artifacts/42nejmba_Stylist%20Outreach%20Latino.png",
    category: "recruitment",
    headline: "Join the Movement"
  },
  
  // === SUPER SOLDIER EXECUTIVE TIER ===
  {
    id: "super-soldier-female-exec",
    name: "Super Soldier - Female Executive",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/51511a51cf4f87179fe5dcb3b5a15891b64b7058101734cb37a155b133a741b0.png",
    category: "superSoldier",
    headline: "Super Soldier - Power 2 Executive"
  },
  {
    id: "super-soldier-male-exec",
    name: "Super Soldier - Male Executive",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/4b68a18b740fb44a1bbad88404de1f69bc7b7e4f75648d90c6c7070caaf9e69b.png",
    category: "superSoldier",
    headline: "Lead a 25-Soldier Platoon"
  },
  {
    id: "super-soldier-platoon",
    name: "Platoon Meeting",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/a7ef07d2d86a2cfb824a9777a5bda687268654b6017c0587564b3836b8d5f084.png",
    category: "superSoldier",
    headline: "Build Your Platoon - Earn 3% Forever"
  },
  {
    id: "super-soldier-analytics",
    name: "Management Analytics",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/97510108377ad96f1817df0a20bbc224502228cd82ce86e9768e1143b44abab8.png",
    category: "superSoldier",
    headline: "$56K/Month Management Residuals"
  },
  {
    id: "super-soldier-training",
    name: "Leadership Training",
    image: "https://static.prod-images.emergentagent.com/jobs/0acd78cc-374f-4fe5-8588-13a240046999/images/ce7605fa8b3426c0845109c84206c1f33dd9f01947baa9990b3fdc255b37d1c4.png",
    category: "superSoldier",
    headline: "The Power 2 - Limited to 2 Nationwide"
  }
];

/**
 * SocialMediaCards - Professional social media cards organized in collapsible folders
 * Features:
 * - Folder-style categories for organization
 * - Dynamic QR code with referral link
 * - Download with QR overlay
 * - Share via SMS, Email, Social
 */
const SocialMediaCards = ({ userId, userName, referralCode, isSoldier = false }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);
  
  // Get cards for a specific category
  const getCardsByCategory = (categoryId) => {
    return BOOTH_RENT_CARDS.filter(card => card.category === categoryId);
  };

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    if (expandedCategory === categoryId) {
      setExpandedCategory(null);
      setSelectedCard(null);
    } else {
      setExpandedCategory(categoryId);
      const categoryCards = getCardsByCategory(categoryId);
      if (categoryCards.length > 0) {
        setSelectedCard(categoryCards[0]);
      }
    }
  };

  // Generate the referral/booking link
  const getLink = () => {
    if (isSoldier) {
      return `${window.location.origin}/soldiers?ref=${referralCode || userId}&source=social_card`;
    }
    return `${window.location.origin}/book/${userId}?source=social_card`;
  };

  const referralLink = getLink();

  // Generate card with QR code overlay
  const generateCardWithQR = async () => {
    if (!selectedCard) {
      toast.error("Please select a card first");
      return;
    }
    
    setGenerating(true);
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      
      // Load the base image
      const img = new Image();
      img.crossOrigin = "anonymous";
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = selectedCard.image;
      });

      // Set canvas size to match image
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw the base image
      ctx.drawImage(img, 0, 0);

      // Generate QR code as data URL
      const qrSize = Math.min(canvas.width, canvas.height) * 0.2;
      const qrPadding = 20;
      const qrX = canvas.width - qrSize - qrPadding - 20;
      const qrY = qrPadding;

      // Create QR code canvas
      const qrCanvas = document.createElement("canvas");
      const qrCtx = qrCanvas.getContext("2d");
      qrCanvas.width = qrSize + 20;
      qrCanvas.height = qrSize + 20;
      
      // White background for QR
      qrCtx.fillStyle = "white";
      qrCtx.roundRect(0, 0, qrSize + 20, qrSize + 20, 10);
      qrCtx.fill();

      // Generate QR as SVG and draw
      const qrElement = document.getElementById("social-card-qr");
      if (qrElement) {
        const svgData = new XMLSerializer().serializeToString(qrElement);
        const qrImg = new Image();
        qrImg.src = "data:image/svg+xml;base64," + btoa(svgData);
        await new Promise(resolve => { qrImg.onload = resolve; });
        qrCtx.drawImage(qrImg, 10, 10, qrSize, qrSize);
      }

      // Draw QR on main canvas
      ctx.drawImage(qrCanvas, qrX, qrY);

      // Add "Scan to Book" text below QR
      ctx.fillStyle = "white";
      ctx.font = `bold ${qrSize * 0.12}px Arial`;
      ctx.textAlign = "center";
      ctx.fillText(isSoldier ? "Scan to Join" : "Scan to Book", qrX + qrSize/2 + 10, qrY + qrSize + 35);

      // Convert to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `myhairchair-${selectedCard.id}-social-card.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Card downloaded with QR code!");
      }, "image/png");

    } catch (error) {
      console.error("Error generating card:", error);
      toast.error("Failed to generate card. Try downloading the image directly.");
      
      // Fallback: just download the original image
      if (selectedCard) {
        const a = document.createElement("a");
        a.href = selectedCard.image;
        a.download = `myhairchair-${selectedCard.id}-social-card.png`;
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } finally {
      setGenerating(false);
    }
  };

  // Share via SMS
  const shareViaSMS = () => {
    const message = isSoldier 
      ? `Join MyHairChair and earn lifetime commissions! Apply here: ${referralLink}`
      : `Book your next appointment with me on MyHairChair! ${referralLink}`;
    window.open(`sms:?body=${encodeURIComponent(message)}`, "_blank");
    toast.success("Opening SMS...");
  };

  // Share via Email
  const shareViaEmail = () => {
    const subject = isSoldier 
      ? "Join MyHairChair - Earn Lifetime Commissions"
      : `Book Your Appointment with ${userName || "Me"}`;
    const body = isSoldier
      ? `Hey!\n\nI wanted to share an amazing opportunity with you. MyHairChair is looking for top salon owners and stylists to join their founding team.\n\nYou can earn lifetime residuals on every referral!\n\nApply here: ${referralLink}\n\n- ${userName || "Your Friend"}`
      : `Hey!\n\nI'm now booking through MyHairChair - it's easier for both of us! No more app fees, and you can pay me directly via Zelle, PayPal, or Cash App.\n\nBook your next appointment here: ${referralLink}\n\n- ${userName || "Your Stylist"}`;
    
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
    toast.success("Opening email...");
  };

  // Copy link
  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  // Native share
  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: isSoldier ? "Join MyHairChair" : "Book with Me on MyHairChair",
          text: isSoldier 
            ? "Join the MyHairChair founding team and earn lifetime commissions!"
            : "Book your next appointment with me on MyHairChair!",
          url: referralLink
        });
        toast.success("Shared successfully!");
      } catch (error) {
        if (error.name !== "AbortError") {
          copyLink();
        }
      }
    } else {
      copyLink();
    }
  };

  return (
    <div className="space-y-6" data-testid="social-media-cards">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-amber-400" />
            Professional Social Cards
          </h3>
          <p className="text-stone-400 text-sm">Click a category folder to browse and download cards</p>
        </div>
        <Badge className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border-amber-500/30">
          <Sparkles className="w-3 h-3 mr-1" />
          {BOOTH_RENT_CARDS.length} Total Cards
        </Badge>
      </div>

      {/* Category Folders Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {CARD_CATEGORIES.map((category) => {
          const categoryCards = getCardsByCategory(category.id);
          const isExpanded = expandedCategory === category.id;
          const previewImage = categoryCards[0]?.image;
          
          return (
            <Card
              key={category.id}
              onClick={() => toggleCategory(category.id)}
              className={`cursor-pointer transition-all duration-300 overflow-hidden ${
                isExpanded
                  ? "ring-2 ring-amber-500 bg-stone-800"
                  : "bg-stone-800/50 border-stone-700 hover:border-amber-500/50 hover:scale-[1.02]"
              }`}
              data-testid={`category-folder-${category.id}`}
            >
              {/* Folder Preview Image */}
              <div className="relative h-28 overflow-hidden bg-gradient-to-br from-stone-800 to-stone-900">
                {previewImage && (
                  <img
                    src={previewImage}
                    alt={category.name}
                    className="w-full h-full object-cover opacity-60"
                    loading="lazy"
                  />
                )}
                <div className={`absolute inset-0 bg-gradient-to-t ${category.color} opacity-30`} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-4xl">{category.icon}</div>
                </div>
              </div>
              
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <FolderOpen className="w-5 h-5 text-amber-400" />
                    ) : (
                      <Folder className="w-5 h-5 text-stone-400" />
                    )}
                    <span className="font-medium text-white">{category.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-stone-700 text-stone-300 text-xs">
                      {categoryCards.length}
                    </Badge>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-amber-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-stone-500" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Expanded Category Cards View */}
      {expandedCategory && (
        <Card className="bg-stone-900 border-stone-700" data-testid="expanded-category-view">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <span className="text-2xl">{CARD_CATEGORIES.find(c => c.id === expandedCategory)?.icon}</span>
                {CARD_CATEGORIES.find(c => c.id === expandedCategory)?.name}
                <Badge className="bg-amber-500/20 text-amber-400 ml-2">
                  {getCardsByCategory(expandedCategory).length} cards
                </Badge>
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedCategory(null);
                  setSelectedCard(null);
                }}
                className="text-stone-400 hover:text-white"
                data-testid="close-category-btn"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Cards Grid for Selected Category */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {getCardsByCategory(expandedCategory).map((card) => (
                <Card
                  key={card.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCard(card);
                  }}
                  className={`cursor-pointer transition-all duration-300 overflow-hidden ${
                    selectedCard?.id === card.id
                      ? "ring-2 ring-amber-500 bg-stone-800"
                      : "bg-stone-800/50 border-stone-700 hover:border-amber-500/50 hover:scale-[1.02]"
                  }`}
                  data-testid={`card-${card.id}`}
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-stone-700">
                    <img
                      src={card.image}
                      alt={card.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {selectedCard?.id === card.id && (
                      <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                        <Check className="w-8 h-8 text-amber-400" />
                      </div>
                    )}
                  </div>
                  <CardContent className="p-2">
                    <p className="text-xs text-stone-300 truncate">{card.name}</p>
                    {card.headline && (
                      <p className="text-[10px] text-amber-400 truncate">{card.headline}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Selected Card Preview & Actions */}
            {selectedCard && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4 border-t border-stone-700">
                {/* Card Preview with QR */}
                <Card className="bg-stone-800/50 border-stone-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-base">Preview with Your QR Code</CardTitle>
                    <CardDescription className="text-stone-400 text-sm">
                      Your personalized link: {referralLink.substring(0, 40)}...
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="relative rounded-xl overflow-hidden bg-stone-900">
                      <img
                        src={selectedCard.image}
                        alt={selectedCard.name}
                        className="w-full h-auto"
                        onError={(e) => {
                          console.error("Image failed to load:", selectedCard.image);
                          e.target.style.display = 'none';
                        }}
                      />
                      {/* QR Code Overlay - Upper Right Corner */}
                      <div className="absolute top-4 right-4 bg-white p-2 rounded-lg shadow-xl">
                        <QRCode
                          id="social-card-qr"
                          value={referralLink}
                          size={80}
                          level="H"
                        />
                        <p className="text-[8px] text-center text-black font-medium mt-1">
                          {isSoldier ? "Scan to Join" : "Scan to Book"}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Share Actions */}
                <Card className="bg-stone-800/50 border-stone-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <Share2 className="w-5 h-5 text-amber-400" />
                      Share This Card
                    </CardTitle>
                    <CardDescription className="text-stone-400 text-sm">
                      Download with QR or share your link directly
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Download Button */}
                    <Button
                      onClick={generateCardWithQR}
                      disabled={generating}
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold h-12"
                      data-testid="download-card-btn"
                    >
                      {generating ? (
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-5 h-5 mr-2" />
                      )}
                      Download Card with QR Code
                    </Button>

                    {/* Share Options */}
                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        variant="outline"
                        onClick={shareViaSMS}
                        className="border-stone-600 text-white hover:bg-stone-700"
                        data-testid="share-sms-btn"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        SMS
                      </Button>
                      <Button
                        variant="outline"
                        onClick={shareViaEmail}
                        className="border-stone-600 text-white hover:bg-stone-700"
                        data-testid="share-email-btn"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Email
                      </Button>
                      <Button
                        variant="outline"
                        onClick={copyLink}
                        className="border-stone-600 text-white hover:bg-stone-700"
                        data-testid="copy-link-btn"
                      >
                        {copied ? (
                          <Check className="w-4 h-4 mr-2 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4 mr-2" />
                        )}
                        {copied ? "Copied!" : "Copy Link"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={nativeShare}
                        className="border-stone-600 text-white hover:bg-stone-700"
                        data-testid="native-share-btn"
                      >
                        <Share2 className="w-4 h-4 mr-2" />
                        Share
                      </Button>
                    </div>

                    {/* Your Link */}
                    <div className="p-3 bg-stone-900 rounded-lg">
                      <p className="text-xs text-stone-400 mb-1">Your personalized link:</p>
                      <div className="flex items-center gap-2">
                        <code className="text-amber-400 text-xs flex-1 truncate">{referralLink}</code>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={copyLink}
                          className="h-7 px-2"
                        >
                          {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                        </Button>
                      </div>
                    </div>

                    {/* Tip */}
                    <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                      <p className="text-amber-400 text-xs">
                        <strong>Pro Tip:</strong> Post these cards on Instagram Stories with your link in bio, 
                        or send directly via DM to potential {isSoldier ? "recruits" : "clients"}!
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SocialMediaCards;
